import { createRef, memo, useCallback, useState, useEffect, useMemo } from 'react';

import CardForm from 'components/CardForm';
import ProductsListModal from 'components/ProductsListModal';
import SelectedProductItemCardForm from 'components/SelectedProductItemCardForm';
import { useFormikContext } from 'formik';
import useDebounce from 'hooks/useDebounce';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { orderBumpService } from 'services';
import { IModal } from 'shared';
import { Button } from 'shared/Houston';
import { SelectProductItemSkeleton } from 'shared/SelectProductItemSkeleton';
import { orderBumpFormActions } from 'store/features';
import { selectMainProduct, selectMediaQuery } from 'store/selectors';
import { enSearchParams } from 'types/enums';
import { IProduct } from 'types/interfaces';

import SearchOutline from '@eduzz/houston-icons/SearchOutline';

const MainProductSelection = () => {
  const product = useSelector(selectMainProduct);
  const refModal = createRef<IModal>();
  const dispatch = useDispatch();
  const formContext = useFormikContext();
  const media = useSelector(selectMediaQuery);
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();
  const [mainProduct, setMainProduct] = useState<Omit<IProduct, 'children'> | null>(null);

  const term = useDebounce(searchParams.get(enSearchParams.termProductMain));
  const ownerContentId = mainProduct?.contentId;

  const { data, fetchNextPage, isSuccess, isFetchingNextPage, isFetching, isLoading } = useInfiniteQuery({
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: ['mainProductList', term],
    queryFn(config) {
      return orderBumpService.getValidMainProducts({ ...config, term });
    },
    getNextPageParam({ lastPage, page }) {
      if (lastPage - page <= 0) {
        return;
      }

      return ++page;
    }
  });

  const productsList = useMemo(() => {
    return data?.pages.flatMap(({ data }) => data);
  }, [data?.pages]);

  const handleCloseModal = () => {
    refModal.current?.closeModal();
    queryClient.cancelQueries({ queryKey: ['mainProductList', term] });
  };

  const addMainProduct = useCallback(
    (products: IProduct[]) => {
      const mainProduct = products[0];

      formContext.setFieldValue('contentId', mainProduct.contentId);
      formContext.setFieldValue('offerId', mainProduct?.offerId);
      dispatch(orderBumpFormActions.setMainProduct(mainProduct));
      orderBumpService.removeAllBumps();

      refModal.current?.closeModal();
    },
    [refModal, formContext, dispatch]
  );

  useEffect(() => {
    setMainProduct(product as Omit<IProduct, 'children'>);
  }, [product]);

  return (
    <>
      <CardForm
        title='Produto principal'
        subtitle='Selecione o produto que terá um Order Bump'
        action={
          <Button startIcon={<SearchOutline />} onClick={() => refModal.current?.openModal()}>
            Selecionar Produto Principal
          </Button>
        }
      />

      {mainProduct ? (
        <SelectedProductItemCardForm product={mainProduct} />
      ) : (
        <CardForm>
          <SelectProductItemSkeleton loading={isLoading} />
        </CardForm>
      )}

      <ProductsListModal
        isInitialized={isSuccess}
        title={Boolean(media.md) ? 'Selecionar Produto' : 'Selecionar Produto Principal'}
        subtitle={Boolean(media.md) ? '' : 'Selecione um produto que terá a(s) oferta(s) de Order Bump'}
        products={productsList || []}
        totalProducts={data?.pages[0].total}
        closeList={handleCloseModal}
        addList={addMainProduct}
        ref={refModal}
        selectionProducts={ownerContentId ? [{ contentId: ownerContentId, offerId: mainProduct?.offerId }] : []}
        isEmptyProductList={isSuccess && !productsList?.length}
        isFetchingNextPage={isFetchingNextPage}
        onInView={fetchNextPage}
        isFetchingByTerm={!isFetchingNextPage && !!term && isFetching}
        termSearch={enSearchParams.termProductMain}
      />
    </>
  );
};

export default memo(MainProductSelection);
